import {
  Component,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  computed,
  signal,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  input,
  output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { AgentChatViewScrollView } from "./ai-chat-view-scroll-view";
import { AgentChatViewInputContainer } from "./ai-chat-view-input-container";
import { AgentChatViewFeather } from "./ai-chat-view-feather";
import { Message } from "@ag-ui/client";
import { cn } from "../utils/utils";
import { ResizeObserverService } from "../utils/resize-observer";
import { AgentChatViewHandlers } from "./ai-chat-view-handlers";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  standalone: true,
  selector: "agent-chat-view",
  imports: [CommonModule, AgentChatViewScrollView, AgentChatViewInputContainer, AgentChatViewFeather],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [ResizeObserverService, AgentChatViewHandlers],
  template: `
    <div [class]="computedClass()">
      <!-- ScrollView -->
      <agent-chat-view-scroll-view
        [autoScroll]="autoScrollSignal()"
        [inputContainerHeight]="inputContainerHeight()"
        [isResizing]="isResizing()"
        [messages]="messagesValue()"
        [messageViewClass]="messageViewClass()"
        [showCursor]="showCursorSignal()"
        (assistantMessageThumbsUp)="assistantMessageThumbsUp.emit($event)"
        (assistantMessageThumbsDown)="assistantMessageThumbsDown.emit($event)"
        (assistantMessageReadAloud)="assistantMessageReadAloud.emit($event)"
        (assistantMessageRegenerate)="assistantMessageRegenerate.emit($event)"
        (userMessageCopy)="userMessageCopy.emit($event)"
        (userMessageEdit)="userMessageEdit.emit($event)"
      >
      </agent-chat-view-scroll-view>

      <!-- Feather effect -->
      <agent-chat-view-feather [inputClass]="featherClass()"> </agent-chat-view-feather>

      <!-- Bottom panel: HITL content + input, measured as one unit for scroll padding -->
      <div #bottomPanel class="achat-bottom-panel">
        <ng-content select="[chatAfterMessages]"></ng-content>
        <agent-chat-view-input-container #inputContainerRef [inputContainerClass]="inputContainerClass()">
        </agent-chat-view-input-container>
      </div>
    </div>
  `,
})
export class AgentChatView implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  messages = input<Message[]>([]);

  autoScroll = input<boolean>(true);

  showCursor = input<boolean>(false);

  messageViewClass = input<string | undefined>(undefined);

  scrollToBottomButtonClass = input<string | undefined>(undefined);

  inputContainerClass = input<string | undefined>(undefined);

  featherClass = input<string | undefined>(undefined);

  disclaimerText = input<string | undefined>(undefined);

  disclaimerClass = input<string | undefined>(undefined);

  assistantMessageThumbsUp = output<{ message: Message }>();

  assistantMessageThumbsDown = output<{ message: Message }>();

  assistantMessageReadAloud = output<{ message: Message }>();

  assistantMessageRegenerate = output<{ message: Message }>();

  userMessageCopy = output<{ message: Message }>();

  userMessageEdit = output<{ message: Message }>();

  @ViewChild("inputContainerRef", { read: AgentChatViewInputContainer })
  inputContainerRef?: AgentChatViewInputContainer;

  @ViewChild("bottomPanel")
  bottomPanelRef?: ElementRef;

  protected messagesValue = computed(() => this.messages());

  protected autoScrollSignal = computed(() => this.autoScroll());

  protected showCursorSignal = computed(() => this.showCursor());

  protected inputContainerHeight = signal<number>(0);

  protected isResizing = signal<boolean>(false);

  protected computedClass = computed(() => cn("achat-view"));

  private destroy$ = new Subject<void>();

  private resizeTimeoutRef?: number;

  constructor(
    private resizeObserverService: ResizeObserverService,
    private handlers: AgentChatViewHandlers,
  ) {}

  ngOnInit(): void {
    this.handlers.hasAssistantThumbsUpHandler.set(true);
    this.handlers.hasAssistantThumbsDownHandler.set(true);
    this.handlers.hasAssistantReadAloudHandler.set(true);
    this.handlers.hasAssistantRegenerateHandler.set(true);
    this.handlers.hasUserCopyHandler.set(true);
    this.handlers.hasUserEditHandler.set(true);
  }

  ngOnChanges(): void {
    this.handlers.hasAssistantThumbsUpHandler.set(true);
    this.handlers.hasAssistantThumbsDownHandler.set(true);
    this.handlers.hasAssistantReadAloudHandler.set(true);
    this.handlers.hasAssistantRegenerateHandler.set(true);
    this.handlers.hasUserCopyHandler.set(true);
    this.handlers.hasUserEditHandler.set(true);
  }

  ngAfterViewInit(): void {
    const measureAndObserve = () => {
      if (!this.bottomPanelRef) {
        return false;
      }

      const el = this.bottomPanelRef.nativeElement as HTMLElement;
      const measuredHeight = el.offsetHeight;
      if (measuredHeight === 0) {
        return false;
      }

      this.inputContainerHeight.set(measuredHeight);

      this.resizeObserverService
        .observeElement(this.bottomPanelRef, 0, 250)
        .pipe(takeUntil(this.destroy$))
        .subscribe((state) => {
          const newHeight = state.height;
          if (newHeight !== this.inputContainerHeight() && newHeight > 0) {
            this.inputContainerHeight.set(newHeight);
            this.isResizing.set(true);
            if (this.resizeTimeoutRef) {
              clearTimeout(this.resizeTimeoutRef);
            }
            this.resizeTimeoutRef = window.setTimeout(() => {
              this.isResizing.set(false);
              this.resizeTimeoutRef = undefined;
            }, 250);
          }
        });

      return true;
    };

    if (!measureAndObserve()) {
      let attempts = 0;
      const maxAttempts = 10;
      const retry = () => {
        attempts++;
        if (measureAndObserve()) {
          // Success
        } else if (attempts < maxAttempts) {
          const delay = 50 * Math.pow(2, Math.min(attempts - 1, 4));
          setTimeout(retry, delay);
        }
      };
      setTimeout(retry, 50);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeTimeoutRef) {
      clearTimeout(this.resizeTimeoutRef);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
